"use client";
import React, { useMemo } from "react";
import { InfiniteData } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { DataTable } from "modules/common/components/Table/DataTable";
import { ResponsiveCardSizing } from "utils/styles";
import TradePreviewCardSkeleton from "modules/common/components/skeletons/trade/TradePreviewCardSkeleton";
import { ResponsiveCardDisplayGrid } from "modules/common/components/grids/ResponsiveCardDisplayGrid";
import { CityPagedItem } from "modules/city/model/cityCards";
import { useCityMatchedCardColumns } from "modules/city/hooks/useCityMatchedCardColumns";
import { CityCardItem } from "./CityCardItem";
import { tradeItemKey } from "modules/tradeLists/utils/tradeItemKey";
import { dedupeByKey } from "utils/dedupeByKey";

interface ItemsData {
  items: CityPagedItem[];
}

interface Props<T extends ItemsData> {
  data: InfiniteData<T> | undefined;
  isPending: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  activeView: { name: string };
  /** Limits rendered preview items and disables infinite loading. */
  maxItems?: number;
  /** Result-set identity; scroll resets only when it changes. */
  listKey?: string;
}

const CityMatchedItemsView = <T extends ItemsData>({
  data,
  isPending,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  activeView,
  maxItems,
  listKey,
}: Props<T>) => {
  const columns = useCityMatchedCardColumns();
  const flattenedData = useMemo(() => {
    // Page-number pagination can return the same item twice across adjacent
    // pages when the underlying order shifts between fetches, which collides
    // on the React/table row key. Dedupe by the same key used for rendering.
    const allItems = dedupeByKey(
      data?.pages.flatMap((page) => page.items) ?? [],
      tradeItemKey
    );
    return maxItems ? allItems.slice(0, maxItems) : allItems;
  }, [data, maxItems]);
  const effectiveHasNextPage = maxItems ? false : hasNextPage;

  const table = useReactTable({
    data: flattenedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (item) => tradeItemKey(item),
  });

  return (
    <div className="flex h-full flex-col">
      {activeView.name === "Table" ? (
        <DataTable
          table={table}
          isLoading={isPending}
          fetchNextPage={fetchNextPage}
          hasNextPage={effectiveHasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          listKey={listKey}
          rowCount={table.getRowModel().rows.length || 20}
          containerClassName="relative h-full overflow-y-auto max-h-none"
          emptyMessage="No matched trades found"
          emptyDescription="Try adjusting your filters or search criteria"
          // A capped preview is not the end of anything — the "View all" link is.
          showEndOfList={!maxItems}
        />
      ) : (
        <ResponsiveCardDisplayGrid
          data={flattenedData}
          isLoading={isPending}
          fetchNextPage={fetchNextPage}
          hasNextPage={effectiveHasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          listKey={listKey}
          renderItem={(item, index, cols) => (
            <CityCardItem
              item={item}
              sizing={ResponsiveCardSizing}
              priority={index < cols * 2}
              fromMatch
            />
          )}
          renderSkeleton={() => (
            <TradePreviewCardSkeleton width="100%" fromMatch />
          )}
          getItemKey={tradeItemKey}
          emptyMessage="No matched trades found"
          containerClassName="relative h-full overflow-y-auto max-h-none"
          showEndOfList={!maxItems}
        />
      )}
    </div>
  );
};

export default CityMatchedItemsView;
